import React, { Fragment, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import { currentPageAccess } from "../utils/library";

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});

const API_BASE = WEBSERVICE;

const apiGet = (url) =>
  fetch(url, { headers: authHeaders() }).then((r) => {
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  });

const apiPost = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then((r) => {
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  });

const apiDelete = (url) =>
  fetch(url, { method: "DELETE", headers: authHeaders() }).then((r) => {
    if (!r.ok) throw new Error("Request failed");
    return r.json();
  });

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function formatSchedule(schedules) {
  if (!schedules) return "Always";
  const { dayOfWeek, startTime, endTime } = schedules;
  const day =
    dayOfWeek != null && dayOfWeek >= 0 && dayOfWeek <= 6
      ? DAY_NAMES[dayOfWeek]
      : null;
  if (!day && !startTime) return "Always";
  if (day && startTime && endTime) return `${day} ${startTime}–${endTime}`;
  if (day) return day;
  return "Always";
}

function normalizeCampaign(c) {
  return {
    id: c.campaignId,
    name: c.campaignName,
    type: c.rewardType,
    rate: c.rewardRate,
    minPurchase: c.minPurchase,
    maxReward: c.maxReward,
    startDate: c.startDate ? c.startDate.split("T")[0] : "",
    endDate: c.endDate ? c.endDate.split("T")[0] : "",
    status: c.status,
    schedule: formatSchedule(c.schedules),
    schedules: c.schedules,
    scheduleId: c.schedules?.scheduleId || null,
  };
}

// ─────────────────────────────────────────────────────────────────
// Style tokens
// ─────────────────────────────────────────────────────────────────
const CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "20px 22px",
  marginBottom: "16px",
};

const LABEL_SM = {
  fontSize: "10px",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: ".7px",
  color: "var(--text-color)",
  opacity: 0.4,
  marginBottom: "6px",
  display: "block",
};

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        background: type === "success" ? "#10b981" : "#ef4444",
        color: "#fff",
        padding: "12px 20px",
        borderRadius: "12px",
        fontSize: "13px",
        fontWeight: "600",
        boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        animation: "slideUp .2s ease",
      }}
    >
      <i
        className={`mdi ${
          type === "success" ? "mdi-check-circle" : "mdi-alert-circle"
        }`}
      />
      {msg}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    active: { bg: "rgba(16,185,129,.12)", color: "#10b981", label: "Active" },
    scheduled: {
      bg: "rgba(245,158,11,.12)",
      color: "#f59e0b",
      label: "Scheduled",
    },
    paused: { bg: "rgba(100,116,139,.12)", color: "#64748b", label: "Paused" },
    ended: { bg: "rgba(239,68,68,.12)", color: "#ef4444", label: "Ended" },
    draft: { bg: "rgba(148,163,184,.12)", color: "#94a3b8", label: "Draft" },
    expired: { bg: "rgba(239,68,68,.12)", color: "#ef4444", label: "Expired" },
  };
  const s = cfg[status?.toLowerCase()] || cfg.paused;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        background: s.bg,
        color: s.color,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: s.color,
          ...(status?.toLowerCase() === "active"
            ? { boxShadow: `0 0 6px ${s.color}` }
            : {}),
        }}
      />
      {s.label}
    </span>
  );
}

function RewardTypeBadge({ type }) {
  const isCashback = type === "cashback";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 9px",
        borderRadius: "6px",
        fontSize: "11px",
        fontWeight: "600",
        background: isCashback
          ? "rgba(99,102,241,.12)"
          : "rgba(20,184,166,.12)",
        color: isCashback ? "#818cf8" : "#2dd4bf",
        border: `1px solid ${
          isCashback ? "rgba(99,102,241,.2)" : "rgba(20,184,166,.2)"
        }`,
      }}
    >
      <i
        className={`mdi ${
          isCashback ? "mdi-cash-refund" : "mdi-ticket-percent"
        }`}
        style={{ fontSize: 11 }}
      />
      {isCashback ? "Cashback" : "Store Credit"}
    </span>
  );
}

function CampaignCard({ campaign, onEdit, onPause, onDelete, isReadOnly }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isPaused = campaign.status?.toLowerCase() === "paused";

  return (
    <div
      style={{
        ...CARD,
        marginBottom: 0,
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap",
        transition: "border-color .15s",
        cursor: "default",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.borderColor =
          "color-mix(in srgb,var(--btn-color) 30%,transparent)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.borderColor = "var(--body-color-less2)")
      }
    >
      {/* Icon */}
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          flexShrink: 0,
          background:
            campaign.type === "cashback"
              ? "rgba(99,102,241,.12)"
              : "rgba(20,184,166,.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          color: campaign.type === "cashback" ? "#818cf8" : "#2dd4bf",
        }}
      >
        <i
          className={`mdi ${
            campaign.type === "cashback"
              ? "mdi-cash-refund"
              : "mdi-ticket-percent"
          }`}
        />
      </div>

      {/* Name & type */}
      <div style={{ flex: "1 1 160px", minWidth: 0 }}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "var(--text-color)",
            marginBottom: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {campaign.name}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <RewardTypeBadge type={campaign.type} />
          <span
            style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5 }}
          >
            {campaign.rate}% reward
          </span>
        </div>
      </div>

      {/* Schedule */}
      <div style={{ flex: "0 0 auto", textAlign: "center", minWidth: 80 }}>
        <span style={LABEL_SM}>Schedule</span>
        <div
          style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7 }}
        >
          {campaign.schedule || "Always"}
        </div>
      </div>

      {/* Status */}
      <div style={{ flex: "0 0 auto" }}>
        <StatusBadge status={campaign.status} />
      </div>

      {/* Actions menu */}
      {!isReadOnly && (
        <div style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid var(--body-color-less2)",
              background: "transparent",
              color: "var(--text-color)",
              opacity: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 16,
              transition: "opacity .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = ".5")}
          >
            <i className="mdi mdi-dots-vertical" />
          </button>
          {menuOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                zIndex: 100,
                background: "var(--body-color-less)",
                border: "1px solid var(--body-color-less2)",
                borderRadius: 10,
                boxShadow: "0 8px 24px rgba(0,0,0,.25)",
                minWidth: 140,
                overflow: "hidden",
                animation: "dropFade .15s ease",
              }}
            >
              {[
                {
                  icon: "mdi-pencil",
                  label: "Edit",
                  action: onEdit,
                  color: "var(--text-color)",
                },
                {
                  icon: isPaused ? "mdi-play" : "mdi-pause-circle",
                  label: isPaused ? "Activate" : "Pause",
                  action: onPause,
                  color: "#f59e0b",
                },
                {
                  icon: "mdi-trash-can",
                  label: "Delete",
                  action: onDelete,
                  color: "#ef4444",
                },
              ].map(({ icon, label, action, color }) => (
                <div
                  key={label}
                  onClick={() => {
                    setMenuOpen(false);
                    action && action();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color,
                    cursor: "pointer",
                    transition: "background .1s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      "var(--body-color-less2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <i className={`mdi ${icon}`} style={{ fontSize: 15 }} />
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────
export default function Rewards() {
  const history = useHistory();
  const isReadOnly = currentPageAccess() === "READ";
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ msg: "", type: "success" });

  const showMsg = (type, msg) => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3000);
  };

  const fetchCampaigns = async () => {
    const merchantId = getMerchantId();
    try {
      const res = await apiGet(
        `${API_BASE}/campaigns/merchant?merchantId=${merchantId}&status=all`
      );
      if (res.status && Array.isArray(res.data)) {
        setCampaigns(res.data.map(normalizeCampaign));
      } else {
        setCampaigns([]);
      }
    } catch {
      showMsg("error", "Failed to load campaigns");
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handlePause = async (campaign) => {
    if (isReadOnly) {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    const isPaused = campaign.status?.toLowerCase() === "paused";
    const endpoint = isPaused ? "activate" : "pause";
    try {
      const res = await apiPost(
        `${API_BASE}/campaigns/${campaign.id}/${endpoint}`,
        {}
      );
      if (res.status) {
        showMsg(
          "success",
          isPaused
            ? `"${campaign.name}" activated`
            : `"${campaign.name}" paused`
        );
        fetchCampaigns();
      } else {
        showMsg("error", res.message || "Operation failed");
      }
    } catch {
      showMsg("error", "Request failed. Please try again.");
    }
  };

  const handleDelete = async (campaign) => {
    if (isReadOnly) {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    if (!window.confirm(`Delete "${campaign.name}"?`)) return;
    try {
      const payload = {
        merchantId: getMerchantId(),
        campaignId: campaign.id,
      };

      if (campaign.scheduleId !== null && campaign.scheduleId !== undefined) {
        payload.scheduleId = campaign.scheduleId;
      }

      const res = await apiPost(
        `${API_BASE}/campaigns/createreward?action=DELETE`,
        payload
      );

      if (res.status) {
        showMsg("success", `"${campaign.name}" deleted`);
        setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
      } else {
        showMsg("error", res.message || "Delete failed");
      }
    } catch {
      showMsg("error", "Request failed. Please try again.");
    }
  };

  const activeCampaigns = campaigns.filter(
    (c) => c.status?.toLowerCase() === "active"
  );
  const otherCampaigns = campaigns.filter((c) => {
    const s = c.status?.toLowerCase();
    return s === "scheduled" || s === "paused";
  });

  return (
    <Fragment>
      <style>{`
        @keyframes slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dropFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shimmer{0%{opacity:.4}50%{opacity:.8}100%{opacity:.4}}
        .rw-grid>*{min-width:0;}
        @media(max-width:860px){.rw-grid{grid-template-columns:1fr!important;}}
      `}</style>

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={{ padding: "32px 24px 80px" }}>
            <Toast msg={toast.msg} type={toast.type} />

            {/* ── Page header ── */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 28,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    color: "var(--text-color)",
                    margin: 0,
                    letterSpacing: "-.3px",
                  }}
                >
                  Rewards
                </h2>
                <p
                  style={{
                    fontSize: "13px",
                    color: "var(--text-color)",
                    opacity: 0.45,
                    margin: "6px 0 0",
                  }}
                >
                  Reward customers automatically after payments
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {/* <button
                  onClick={() => history.push("/rewards/analytics")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "9px 18px", borderRadius: 10,
                    border: "1.5px solid var(--body-color-less2)",
                    background: "transparent", color: "var(--text-color)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    opacity: .75, transition: "opacity .15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={e => e.currentTarget.style.opacity = ".75"}
                >
                  <i className="mdi mdi-chart-bar" style={{ fontSize: 15 }} />
                  Analytics
                </button> */}
                {!isReadOnly && (
                  <button
                    onClick={() => history.push("/rewards/create")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: "none",
                      background: "var(--btn-color)",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      boxShadow:
                        "0 0 14px color-mix(in srgb,var(--btn-color) 35%,transparent)",
                      transition: "filter .15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.filter = "brightness(1.12)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.filter = "brightness(1)")
                    }
                  >
                    <i className="mdi mdi-plus" style={{ fontSize: 16 }} />
                    Create Campaign
                  </button>
                )}
              </div>
            </div>

            {/* ── Two-column grid ── */}
            <div
              className="rw-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,62fr) minmax(0,38fr)",
                gap: 20,
                alignItems: "start",
              }}
            >
              {/* LEFT — Campaigns */}
              <div>
                {/* Active campaigns */}
                <div style={CARD}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <i
                        className="mdi mdi-lightning-bolt"
                        style={{ fontSize: 14, color: "#10b981" }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-color)",
                          opacity: 0.7,
                        }}
                      >
                        Active Campaigns
                      </span>
                      {!loading && (
                        <span
                          style={{
                            background: "rgba(16,185,129,.12)",
                            color: "#10b981",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "1px 8px",
                          }}
                        >
                          {activeCampaigns.length}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => history.push("/rewards/campaigns")}
                      style={{
                        fontSize: 12,
                        color: "var(--btn-color)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                        opacity: 0.8,
                      }}
                    >
                      View all →
                    </button>
                  </div>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      {[1, 2].map((i) => (
                        <div
                          key={i}
                          style={{
                            height: 72,
                            borderRadius: 10,
                            background: "var(--body-color-less2)",
                            animation: "shimmer 1.4s ease infinite",
                          }}
                        />
                      ))}
                    </div>
                  ) : activeCampaigns.length === 0 ? (
                    <div
                      style={{
                        padding: "32px 16px",
                        textAlign: "center",
                        border: "1.5px dashed var(--body-color-less2)",
                        borderRadius: 10,
                      }}
                    >
                      <i
                        className="mdi mdi-lightning-bolt"
                        style={{
                          fontSize: 32,
                          color: "var(--text-color)",
                          opacity: 0.2,
                          display: "block",
                          marginBottom: 10,
                        }}
                      />
                      <p
                        style={{
                          fontSize: 13,
                          color: "var(--text-color)",
                          opacity: 0.4,
                          margin: 0,
                        }}
                      >
                        No active campaigns yet.
                      </p>
                      {!isReadOnly && (
                        <button
                          onClick={() => history.push("/rewards/create")}
                          style={{
                            marginTop: 14,
                            padding: "8px 18px",
                            borderRadius: 10,
                            border: "none",
                            background: "var(--btn-color)",
                            color: "#fff",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <i
                            className="mdi mdi-plus"
                            style={{ fontSize: 14 }}
                          />
                          Create Campaign
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      {activeCampaigns.map((c) => (
                        <CampaignCard
                          key={c.id}
                          campaign={c}
                          onEdit={() => history.push(`/rewards/edit/${c.id}`)}
                          onPause={() => handlePause(c)}
                          onDelete={() => handleDelete(c)}
                          isReadOnly={isReadOnly}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Other campaigns */}
                {!loading && otherCampaigns.length > 0 && (
                  <div style={CARD}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 16,
                      }}
                    >
                      <i
                        className="mdi mdi-clock"
                        style={{ fontSize: 14, color: "#f59e0b" }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-color)",
                          opacity: 0.7,
                        }}
                      >
                        Scheduled & Paused
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      {otherCampaigns.map((c) => (
                        <CampaignCard
                          key={c.id}
                          campaign={c}
                          onEdit={() => history.push(`/rewards/edit/${c.id}`)}
                          onPause={() => handlePause(c)}
                          onDelete={() => handleDelete(c)}
                          isReadOnly={isReadOnly}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* How it works */}
                <div
                  style={{
                    ...CARD,
                    background: "transparent",
                    border: "1px dashed var(--body-color-less2)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 14,
                    }}
                  >
                    <i
                      className="mdi mdi-information"
                      style={{
                        fontSize: 14,
                        color: "var(--text-color)",
                        opacity: 0.4,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text-color)",
                        opacity: 0.4,
                        textTransform: "uppercase",
                        letterSpacing: ".6px",
                      }}
                    >
                      How it works
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {[
                      {
                        icon: "mdi-plus-circle",
                        color: "var(--btn-color)",
                        step: "1",
                        text: "Create a reward campaign",
                      },
                      {
                        icon: "mdi-account",
                        color: "#10b981",
                        step: "2",
                        text: "Customers earn rewards on payment",
                      },
                      {
                        icon: "mdi-wallet",
                        color: "#f59e0b",
                        step: "3",
                        text: "Customers redeem at checkout",
                      },
                    ].map(({ icon, color, step, text }) => (
                      <div
                        key={step}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flex: "1 1 160px",
                        }}
                      >
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            flexShrink: 0,
                            background: `color-mix(in srgb,${color} 12%,transparent)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            color,
                          }}
                        >
                          <i className={`mdi ${icon}`} />
                        </div>
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--text-color)",
                            opacity: 0.55,
                            lineHeight: 1.4,
                          }}
                        >
                          {text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT — Summary */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  position: "sticky",
                  top: 24,
                }}
              >
                {/* Rewards summary card */}
                <div style={CARD}>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: ".7px",
                      color: "var(--text-color)",
                      opacity: 0.4,
                      marginBottom: 16,
                    }}
                  >
                    Campaigns Overview
                  </div>
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          style={{
                            height: 40,
                            borderRadius: 8,
                            background: "var(--body-color-less2)",
                            animation: "shimmer 1.4s ease infinite",
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                      }}
                    >
                      {[
                        {
                          icon: "mdi-flash",
                          color: "#10b981",
                          label: "Active",
                          value: campaigns.filter(
                            (c) => c.status?.toLowerCase() === "active"
                          ).length,
                        },
                        {
                          icon: "mdi-pause-circle",
                          color: "#64748b",
                          label: "Paused",
                          value: campaigns.filter(
                            (c) => c.status?.toLowerCase() === "paused"
                          ).length,
                        },
                        {
                          icon: "mdi-view-list",
                          color: "var(--btn-color)",
                          label: "Total Campaigns",
                          value: campaigns.length,
                        },
                      ].map(({ icon, color, label, value }, idx, arr) => (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "12px 0",
                            borderBottom:
                              idx < arr.length - 1
                                ? "1px solid var(--body-color-less2)"
                                : "none",
                          }}
                        >
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              flexShrink: 0,
                              background: `color-mix(in srgb,${color} 12%,transparent)`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 16,
                              color,
                            }}
                          >
                            <i className={`mdi ${icon}`} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 11,
                                color: "var(--text-color)",
                                opacity: 0.45,
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: "var(--text-color)",
                                letterSpacing: "-.3px",
                              }}
                            >
                              {value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick links */}
                <div style={CARD}>
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: ".7px",
                      color: "var(--text-color)",
                      opacity: 0.4,
                      marginBottom: 12,
                    }}
                  >
                    Quick Links
                  </div>
                  {[
                    ...(isReadOnly
                      ? []
                      : [
                          {
                            icon: "mdi-plus-circle",
                            label: "Create Campaign",
                            action: () => history.push("/rewards/create"),
                            color: "var(--btn-color)",
                          },
                        ]),
                    {
                      icon: "mdi-format-list-bulleted",
                      label: "All Campaigns",
                      action: () => history.push("/rewards/campaigns"),
                      color: "#6366f1",
                    },
                    {
                      icon: "mdi-chart-bar",
                      label: "Rewards Analytics",
                      action: () => history.push("/rewards/analytics"),
                      color: "#10b981",
                    },
                  ].map(({ icon, label, action, color }) => (
                    <div
                      key={label}
                      onClick={action}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "9px 0",
                        borderBottom: "1px solid var(--body-color-less2)",
                        cursor: "pointer",
                      }}
                    >
                      <i
                        className={`mdi ${icon}`}
                        style={{ fontSize: 16, color, opacity: 0.8 }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          color: "var(--text-color)",
                          opacity: 0.7,
                          transition: "opacity .15s",
                        }}
                      >
                        {label}
                      </span>
                      <i
                        className="mdi mdi-chevron-right"
                        style={{
                          marginLeft: "auto",
                          fontSize: 15,
                          color: "var(--text-color)",
                          opacity: 0.25,
                        }}
                      />
                    </div>
                  ))}
                  <div style={{ paddingTop: 4 }} />
                </div>

                {/* Reward rules */}
                <div
                  style={{
                    ...CARD,
                    background:
                      "color-mix(in srgb,var(--btn-color) 6%,transparent)",
                    border:
                      "1px solid color-mix(in srgb,var(--btn-color) 18%,transparent)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                  >
                    <i
                      className="mdi mdi-shield-check"
                      style={{
                        fontSize: 18,
                        color: "var(--btn-color)",
                        marginTop: 1,
                      }}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--text-color)",
                          marginBottom: 5,
                        }}
                      >
                        Reward Rules
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          padding: "0 0 0 14px",
                          fontSize: 11,
                          color: "var(--text-color)",
                          opacity: 0.55,
                          lineHeight: 1.7,
                        }}
                      >
                        <li>Highest reward wins — no stacking</li>
                        <li>Cashback is withdrawable by customers</li>
                        <li>Store credit is merchant-specific</li>
                        <li>Rewards are not auto-applied at checkout</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
